import React, { useRef, useState, useEffect, useContext } from 'react';
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import { t } from "i18next";
import SendChatInputIcon from '../../../../components/SVGComponents/SendChatInputIcon';
import themeContext from '../../../../context/theme/ThemeContext';
import studyviewerContext from '../../../../context/studyviewer/StudyviewerContext';
import { NONXRAYMODALITIES } from '../../../../constants/Constants';
import CustomToolTipHost from '../../../../components/CustomToolTipHost/CustomToolTipHost';

const ChatInput = ({ onSendMessage }) => {
  const textAreaRef = useRef(null);

  const {
    handleDSRRegenerate,
    setActiveField,
    inputValue,
    setInputValue,
    registerChatInputRef,
    findingSentence,
    isExternal,
    reportDoneStatusState,
    reportTitle,
    modality,
    maskImages
  } = useContext(studyReportContext);


  useEffect(() => {
    registerChatInputRef(textAreaRef);
  }, [registerChatInputRef]);

  const [height, setHeight] = useState(33);
  const lineHeight = 26.3;
  const maxLines = 4; 
  const maxHeight = lineHeight * maxLines;
  const { theme } = useContext(themeContext);

  useEffect(() => {
    adjustHeight();
  }, [inputValue]);

  useEffect(() => {
    const defaultText = findingSentence.trim() || '';
    setInputValue('');
    if (inputValue === ''  &&  reportDoneStatusState === 0) {
      setInputValue(defaultText);
    }
  }, [findingSentence]);

  const adjustHeight = () => {
    const ta = textAreaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const newH = Math.min(ta.scrollHeight, maxHeight);
    setHeight(newH);
    ta.style.height = `${newH}px`;
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const isTextareaDisabled = reportTitle === "" ||
        (!inputValue.trim() && !isExternal && (Object.values(maskImages).length === 0));

 function getToolTipContent() {
  if (!isTextareaDisabled) return "Send";

  const noTitle = reportTitle === "";
  const noPrompt = !inputValue.trim();
  const noImage = Object.values(maskImages).filter(e => e.isActive).length === 0;
  const needsImage = !isExternal && noImage;

  if (NONXRAYMODALITIES.includes(modality)) {
    if (noTitle && noPrompt && needsImage) {
      return "Missing title, prompt, or image";
    }
    if (noTitle) {
      return "Title is not selected";
    }
    if (noPrompt && needsImage) {
      return "Prompt or image are missing";
    }
    if (noPrompt) {
      return "Prompt is missing";
    }
    if (needsImage) {
      return "Image is missing";
    }
  } else {
    if (noTitle && noPrompt) {
      return "Missing Title and prompt";
    }
    if (noTitle) {
      return "Title is not selected";
    }
    if (noPrompt) {
      return "Prompt is missing";
    }
  }

  return "Required information is missing";
}



  const handleSendMessage = () => {
    if(isTextareaDisabled) return;
    setInputValue(inputValue.trim());
    handleDSRRegenerate();
    
    setHeight(33);
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '33px';   
    }
};


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  


  const styles = {
  container: {
    width: '100%',
    height: isExternal ?'34.21px': '35.21px',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: isExternal ? '4px' : '0',

  },  innerWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    marginLeft : '0.5%'
  },
  inputWrapper: {
    flex: 1,
    backgroundColor: '#FAFAFA',
    border: '1px solid #000',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  textareaContainer: {
    display: 'flex',
    width: '100%',
    overflowY: 'auto',
    overflowY: height >= maxHeight ? 'auto' : 'hidden',
  },
  textarea: {
    flex: 1,
    fontSize: '13px',
    fontFamily: "Segoe UI",
    lineHeight: `${lineHeight}px`,
    padding: '3.5px 10px',
    paddindRight: '14px',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    resize: 'none',
    minHeight: '30px',
    maxHeight: `${maxHeight}px`,
    height: `${height}px`,
    overflowY: height >= maxHeight ? 'auto' : 'hidden',
    boxSizing: 'border-box',
    color: "black",
    marginRight: "3px"
  },
  sendButton: {
    background: 'transparent',
    border: 'none',
    cursor: `${isTextareaDisabled ? 'not-allowed' : 'pointer'}`,
    height : '29px',
    marginLeft: '3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-end',  
}

};



  return (
  <div style={styles.container}>
    <div style={styles.innerWrapper}>
      <div style={styles.inputWrapper}>
        <div style={styles.textareaContainer}>
          <textarea
            ref={textAreaRef}
            placeholder={t("placeholder.no-findings")}
            rows={1}
            value={inputValue}
            onChange={handleInputChange}
            onInput={adjustHeight}
            onKeyPress={handleKeyPress} 
            onFocus={() => setActiveField('chatInput')}
            onBlur={() => setActiveField(null)}
            style={{
              ...styles.textarea,
              height: `${height}px`
            }}
          />
        </div>
      </div>
      <button
        type="button"
        aria-label="Send"
        onClick={handleSendMessage}
        style={{
          ...styles.sendButton,
          color: inputValue.trim() ? '#007bff' : '#999',
        }}
      >
        <CustomToolTipHost 
                  content={getToolTipContent()} 
                  id="Send Button"
                  tooltipProps={{
                    calloutProps: {
                      styles: {
                        beak: { background: "#252525;" },
                        beakCurtain: { background: "#252525;" },
                        calloutMain: { background: "#252525;" },
                        borderRadius: "20%",
                      },
                    },
                  }}
                >
            <SendChatInputIcon color={!isTextareaDisabled? 
                                              theme.palette.activeTool : theme.palette.inactiveTool} />
        </CustomToolTipHost>
      </button>
    </div> 
  </div>
);

};

export default ChatInput;
